import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { ReviewCard, StarRatingInput, StarRating } from '../../components/ReviewCard';
import { getGuestReviews, createReview, getGuestBookings } from '../../lib/api';
import { toast } from 'sonner';
import { Star, Loader2, PenSquare } from 'lucide-react';
import { format, isPast, parseISO } from 'date-fns';

export default function GuestReviews() {
  const [reviews, setReviews] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 0,
    title: '',
    comment: '',
    would_recommend: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, bookingsRes] = await Promise.all([
        getGuestReviews({ limit: 20 }),
        getGuestBookings({ limit: 50 })
      ]);
      
      setReviews(reviewsRes.data.items);
      setCursor(reviewsRes.data.next_cursor);
      setHasMore(reviewsRes.data.has_more);
      
      // Filter bookings that can be reviewed (completed OR checkout passed AND not cancelled AND not already reviewed)
      const reviewableBookings = (bookingsRes.data.items || []).filter(booking => {
        if (booking.has_review) return false;
        if (booking.status === 'cancelled') return false;
        if (booking.status === 'completed') return true;
        
        const checkoutDate = parseISO(booking.check_out);
        return isPast(checkoutDate) && ['paid', 'approved'].includes(booking.status);
      });
      
      setPendingBookings(reviewableBookings);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreReviews = async () => {
    if (!cursor) return;
    try {
      const response = await getGuestReviews({ cursor, limit: 20 });
      setReviews(prev => [...prev, ...response.data.items]);
      setCursor(response.data.next_cursor);
      setHasMore(response.data.has_more);
    } catch (error) {
      toast.error('Failed to load more reviews');
    }
  };

  const handleOpenReviewForm = (booking) => {
    setSelectedBooking(booking);
    setReviewForm({ rating: 0, title: '', comment: '', would_recommend: false });
    setShowReviewForm(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedBooking) return;
    
    if (reviewForm.rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (reviewForm.comment.length < 10) {
      toast.error('Please write at least 10 characters in your review');
      return;
    }

    setSubmitting(true);
    try {
      await createReview({
        booking_id: selectedBooking.id,
        rating: reviewForm.rating,
        title: reviewForm.title || null,
        comment: reviewForm.comment,
        would_recommend: reviewForm.would_recommend || null
      });
      
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      loadData(); // Refresh data
    } catch (error) {
      console.error('Failed to submit review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guest-reviews">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">My Reviews</h1>
        <p className="text-slate-500 mt-1">
          View and manage your property reviews
        </p>
      </div>

      {/* Pending Reviews Section */}
      {pendingBookings.length > 0 && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PenSquare className="w-5 h-5 text-primary" />
              Stays Waiting for Your Review
            </CardTitle>
            <CardDescription>
              Share your experience to help other guests
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingBookings.map((booking) => (
                <div 
                  key={booking.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200"
                >
                  <div>
                    <p className="font-medium text-slate-900">{booking.property_title}</p>
                    <p className="text-sm text-slate-500">
                      {format(parseISO(booking.check_in), 'MMM d')} - {format(parseISO(booking.check_out), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <Button onClick={() => handleOpenReviewForm(booking)} data-testid="write-review-btn">
                    Write Review
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Past Reviews Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            Your Reviews ({reviews.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>You haven&apos;t written any reviews yet.</p>
              <p className="text-sm mt-1">After completing a stay, you can leave a review.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} showProperty />
              ))}
              
              {hasMore && (
                <div className="text-center pt-4">
                  <Button variant="outline" onClick={loadMoreReviews}>
                    Load More
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Form Dialog */}
      <Dialog open={showReviewForm} onOpenChange={setShowReviewForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Write a Review</DialogTitle>
            <DialogDescription>
              {selectedBooking && `Share your experience at ${selectedBooking.property_title}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Rating */}
            <div className="space-y-2">
              <Label>Overall Rating *</Label>
              <StarRatingInput 
                value={reviewForm.rating} 
                onChange={(v) => setReviewForm(prev => ({ ...prev, rating: v }))}
              />
            </div>
            
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="review-title">Title (optional)</Label>
              <input
                id="review-title"
                type="text"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Summarize your experience"
                maxLength={200}
                value={reviewForm.title}
                onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            
            {/* Comment */}
            <div className="space-y-2">
              <Label htmlFor="review-comment">Your Review *</Label>
              <Textarea
                id="review-comment"
                placeholder="Tell us about your stay. What did you like? What could be improved?"
                rows={5}
                maxLength={2000}
                value={reviewForm.comment}
                onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
              />
              <p className="text-xs text-slate-500">{reviewForm.comment.length}/2000 characters</p>
            </div>
            
            {/* Would Recommend */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="would-recommend"
                checked={reviewForm.would_recommend}
                onCheckedChange={(v) => setReviewForm(prev => ({ ...prev, would_recommend: v }))}
              />
              <Label htmlFor="would-recommend" className="cursor-pointer">
                I would recommend this property
              </Label>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button 
                className="flex-1"
                onClick={handleSubmitReview}
                disabled={submitting || reviewForm.rating === 0 || reviewForm.comment.length < 10}
                data-testid="submit-review-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Review
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowReviewForm(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
